/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ResetPassword.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Avatar, Box, IconButton, Link, Typography } from "@mui/material";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { AuthTextField } from "../";
import appMeta from "../../assets/app-meta/appMeta";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";

function ResetPassword({ throttledHandleResetPassword, handleAuthViewChange }) {
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const handleLoginOnClick = () => {
    handleAuthViewChange(appMeta.authView.LOGIN);
  };

  const handleResetPasswordOnClick = () => {
    const otpRegex = /^\d{6}$/;

    if (otpRegex.test(otp)) {
      setOtpError("");
    } else {
      setOtpError("OTP must be a 6-digit number");
      return;
    }

    const passwdMinLength = 8;
    const lowerCaseRegex = /[a-z]/;
    const upperCaseRegex = /[A-Z]/;
    const numberRegex = /\d/;
    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

    if (
      password.length < passwdMinLength ||
      !lowerCaseRegex.test(password) ||
      !upperCaseRegex.test(password) ||
      !numberRegex.test(password) ||
      !specialCharRegex.test(password)
    ) {
      setPasswordError("Password is weak");
    } else {
      setPasswordError("");
    }

    if (password === confirmPassword) {
      setConfirmPasswordError("");
    } else {
      setConfirmPasswordError("Passwords do not match.");
      return;
    }

    throttledHandleResetPassword({ otp, password });
  };

  return (
    <Box sx={styles.mainContainer}>
      <Avatar variant="rounded" sx={styles.avatar}>
        <img height={"100%"} src="deloitteIconBlackLarge.png" alt="Deloitte" />
      </Avatar>

      <Typography style={{ fontWeight: 600 }} component="h1" variant="body1">
        Reset Password
      </Typography>

      <Box sx={styles.fldsContainer}>
        <AuthTextField
          label={"Code"}
          placeHolder={"Code"}
          fldType={appMeta.textFieldTypes.OTP}
          variant={"standard"}
          value={otp}
          setValue={setOtp}
          valueError={otpError}
          setValueError={setOtpError}
          isAutoFocused={true}
          isRequired={true}
          txtFldStyle={{
            mt: 1,
            mb: 2,
          }}
          maxLength={40}
          minLength={6}
        />
        <AuthTextField
          label={"New Password"}
          placeHolder={"New Password"}
          type={"password"}
          fldType={appMeta.textFieldTypes.PASSWORD}
          variant={"standard"}
          value={password}
          inputProps={{
            autocomplete: "new-password",
          }}
          setValue={setPassword}
          valueError={passwordError}
          setValueError={setPasswordError}
          isAutoFocused={false}
          isRequired={true}
          txtFldStyle={{
            mt: 1,
            mb: 2,
          }}
          maxLength={40}
          minLength={6}
        />
        <AuthTextField
          label={"Confirm Password"}
          placeHolder={"Confirm Password"}
          type={"password"}
          fldType={appMeta.textFieldTypes.CONFIRM_PASSWORD}
          variant={"standard"}
          value={confirmPassword}
          passwordValue={password}
          setValue={setConfirmPassword}
          valueError={confirmPasswordError}
          setValueError={setConfirmPasswordError}
          isAutoFocused={false}
          isRequired={true}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleResetPasswordOnClick();
          }}
          txtFldStyle={{
            mt: 1,
            mb: 2,
          }}
          maxLength={40}
          minLength={6}
        />
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CustomButtonV2
            btnName="Reset Password"
            handleOnClick={handleResetPasswordOnClick}
            type="submit"
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <IconButton sx={styles.iconButton} aria-label="back to login">
            <ArrowBackIcon />
          </IconButton>
          <Link
            style={styles.linkStyle}
            href="#"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              handleLoginOnClick();
            }}
          >
            Back to Login
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

ResetPassword.propTypes = {
  throttledHandleResetPassword: PropTypes.func,
  handleAuthViewChange: PropTypes.func,
};

export default ResetPassword;

const styles = {
  mainContainer: {
    width: "100%",
    paddingX: 5,
    paddingY: 4,
    borderRadius: 1,
    backgroundColor: "white",
    border: { sm: "1px solid lightgrey" },
    boxShadow: { sm: "0px 2px 4px rgba(0, 0, 0, 0.1)" },
    boxSizing: "border-box",
  },
  fldsContainer: {
    width: "100%",
  },
  avatar: {
    mb: 3,
    bgcolor: "white",
    width: "100%",
    height: 24,
  },
  iconButton: {
    color: "#1976d2",
    "& svg": {
      fontSize: "20px",
    },
  },
};
